import {
  stopIntercepting,
  tryLoadAndStartRecorder,
} from '@alwaysmeticulous/recorder-loader';
import {useEffect, useRef} from 'react';

import {envIsDev, envIsProd} from '../../config';
import {useViewer} from '../../state/viewer/hooks';
import {isInternalUser, useIsAdminModeActiveAndIsNotLocalEnv} from '../admin';
import {getDatadogRum} from '../datadog';

// Members as of 9/16/2025
// wandb - 547
// coreweave_ml-org - 44
// coreweave1-org - 135
const ApprovedAccountList = [
  'wandb',
  'coreweave_ml-org',
  'coreweave1-org',
  'public-team-private-projects-org',
];

const DD_RUM = getDatadogRum();

// safe to store the token here since it's a FE token and there's no real way to obscure it
const token = 'iz8Ne1LOrhfBfq9MGP5laPHdgJb6HqVVV1X56STr';

function useMeticulousRecorderDev(accountName?: string) {
  const viewer = useViewer();
  const viewerExists = !!viewer;
  const adminModeActive = useIsAdminModeActiveAndIsNotLocalEnv();
  const isExternal = viewer && !isInternalUser(viewer);
  const isInitialized = useRef(false);

  // Track account names used by internal users so that we can audit them and find
  // more accounts that we can approve for recording meticulous sessions
  useEffect(() => {
    if (isExternal || !viewerExists) {
      return;
    }
    if (accountName && !!(viewer?.email ?? '')) {
      DD_RUM.addAction('log_internal_user_account_name', {
        accountName,
        email: viewer?.email,
        isAllowedAccount: ApprovedAccountList.includes(
          accountName.toLowerCase()
        ),
      });
    }
  }, [viewerExists, isExternal, accountName, viewer?.email, viewer]);

  useEffect(() => {
    // we don't need to worry about the hook in CI, we can record everything
    if (!viewerExists || !accountName) {
      return;
    }
    if (adminModeActive || isExternal) {
      stopIntercepting();
      if (adminModeActive) {
        console.log('Stopped meticulous recorder in admin mode');
      } else if (isExternal) {
        console.log('Stopped meticulous recorder in external mode');
      }
    } else if (
      !isInitialized.current &&
      ApprovedAccountList.includes(accountName)
    ) {
      // we try to avoid multiple inits, but it's fine if this calls multiple times
      // it no-ops if active
      // https://weightsandbiases.slack.com/archives/C09BXRYRU2K/p1757978656392079?thread_ts=1757976730.837009&cid=C09BXRYRU2K
      tryLoadAndStartRecorder({
        recordingToken: token,
        isProduction: envIsProd,
      });
      console.log('Started meticulous recorder');

      isInitialized.current = true;
    }
  }, [accountName, adminModeActive, isExternal, viewerExists]);
}

// No-op the meticulous recorder outside of allowed environments
// right now [9/26/2025s] we're only allowing the recorder in dev environments
export const useMeticulousRecorder = envIsDev
  ? useMeticulousRecorderDev
  : () => {};
